import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, FileText, Clock, MessageCircle, X } from "lucide-react";
import { api } from "../lib/api";

interface SearchHit {
  kind: "client" | "invoice" | "shift" | "message";
  id: string;
  title: string;
  subtitle: string | null;
  href: string;
}

const KIND_ICONS = {
  client: Users,
  invoice: FileText,
  shift: Clock,
  message: MessageCircle,
} as const;

const KIND_LABELS = {
  client: "Clients",
  invoice: "Invoices",
  shift: "Shifts",
  message: "Messages",
} as const;

const KIND_ORDER: SearchHit["kind"][] = ["client", "invoice", "shift", "message"];

function groupByKind(hits: SearchHit[]): Record<SearchHit["kind"], SearchHit[]> {
  const empty = { client: [], invoice: [], shift: [], message: [] } as Record<SearchHit["kind"], SearchHit[]>;
  for (const h of hits) empty[h.kind].push(h);
  return empty;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global Cmd+K / Ctrl+K + Escape toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus on open, reset state on close.
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    setQuery("");
    setHits([]);
    setActiveIndex(0);
  }, [open]);

  // Debounced fetch. Bail on empty query.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const handle = window.setTimeout(() => {
      api(`/api/search?q=${encodeURIComponent(q)}`)
        .then((data: { hits: SearchHit[] }) => {
          if (cancelled) return;
          setHits(data.hits ?? []);
          setActiveIndex(0);
        })
        .catch(() => { if (!cancelled) setHits([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 140);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [query, open]);

  const grouped = useMemo(() => groupByKind(hits), [hits]);
  const orderedHits = useMemo(() => {
    const out: SearchHit[] = [];
    for (const kind of KIND_ORDER) for (const h of grouped[kind]) out.push(h);
    return out;
  }, [grouped]);

  const choose = (hit: SearchHit) => {
    setOpen(false);
    navigate(hit.href);
  };

  const onInputKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(0, orderedHits.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const hit = orderedHits[activeIndex];
      if (hit) {
        e.preventDefault();
        choose(hit);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search clients, invoices, shifts, and messages"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0, 0, 0, 0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "10vh 16px 16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 560,
          background: "var(--color-surface)",
          borderRadius: 14,
          border: "1px solid var(--color-border)",
          boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          fontFamily: "inherit",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 14px", borderBottom: "1px solid var(--color-border)",
        }}>
          <Search size={16} color="var(--color-text-muted)" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search clients, invoices, shifts, messages…"
            aria-label="Search query"
            style={{
              flex: 1, border: "none", outline: "none",
              background: "transparent", color: "var(--color-text)",
              fontSize: 14, fontFamily: "inherit",
            }}
          />
          <kbd style={{
            fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
            color: "var(--color-text-muted)",
            padding: "2px 6px", borderRadius: 4,
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
          }}>
            Esc
          </kbd>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close search"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--color-text-muted)", padding: 4, display: "flex",
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {query.trim().length === 0 && (
            <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
              Type to search across clients, invoices, shifts, and messages.
            </div>
          )}
          {query.trim().length > 0 && loading && orderedHits.length === 0 && (
            <div style={{ padding: "24px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
              Searching…
            </div>
          )}
          {query.trim().length > 0 && !loading && orderedHits.length === 0 && (
            <div style={{ padding: "24px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
              No matches for <strong style={{ color: "var(--color-text)" }}>{query}</strong>.
            </div>
          )}
          {orderedHits.length > 0 && KIND_ORDER.map(kind => {
            const group = grouped[kind];
            if (group.length === 0) return null;
            const Icon = KIND_ICONS[kind];
            return (
              <div key={kind} style={{ padding: "6px 0" }}>
                <div style={{
                  padding: "8px 14px 4px",
                  fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                }}>
                  {KIND_LABELS[kind]}
                </div>
                {group.map(hit => {
                  const globalIdx = orderedHits.indexOf(hit);
                  const active = globalIdx === activeIndex;
                  return (
                    <button
                      key={`${hit.kind}-${hit.id}`}
                      type="button"
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                      onClick={() => choose(hit)}
                      style={{
                        width: "100%", textAlign: "left",
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 14px",
                        border: "none",
                        background: active ? "var(--color-primary-light)" : "transparent",
                        color: "var(--color-text)",
                        cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      <Icon size={14} color="var(--color-text-muted)" aria-hidden="true" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {hit.title}
                        </div>
                        {hit.subtitle && (
                          <div style={{ fontSize: 11, color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {hit.subtitle}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
